import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  AUTH_PROVIDER_KEY,
  AZURE_SSO_SESSION_KEY,
  MICROSOFT_AUTH_PROVIDER,
  MICROSOFT_SESSION_USER_KEY,
  MS_LOGIN_EMAIL_KEY,
  MS_LOGIN_PENDING_KEY,
} from "../auth/authSessionKeys";
import { buildLoginUrl } from "../routes/guards/authUtils";

let refresh = false;
let isHandlingSessionExpiry = false;

const SESSION_EXPIRED_MESSAGE = "Session expired. Please log in again.";

const hostname = window.location.hostname; // e.g., tenant1.myapp.com
let subdomain =
  hostname.split(".")[0] === "localhost" ? "mcsms" : hostname.split(".")[0]; // Extracts "tenant1"

export const backend_url = process.env.REACT_APP_BACKEND_URL
  ? process.env.REACT_APP_BACKEND_URL
  : `https://${subdomain}-backend.socialroots-dev.net`; // Constructs the base URL

export const frontend_url = `${window.location.origin}`;

export const baseUrl = `${backend_url}/api/`;

const region = process.env.REACT_APP_REGION || "America/Los_Angeles";

/**
 * Detects session-expired payloads from any API and redirects to login
 * with the current location as `?redirect=...`.
 */
function handleSessionExpired(payload) {
  const message = payload?.message ?? payload?.error ?? payload?.detail;
  const status = payload?.status;
  const isSessionExpired =
    message === SESSION_EXPIRED_MESSAGE ||
    (status === "error" &&
      typeof message === "string" &&
      message.toLowerCase().includes("session expired"));

  if (!isSessionExpired || isHandlingSessionExpiry) {
    return false;
  }

  isHandlingSessionExpiry = true;
  toast.error(
    typeof message === "string" && message.trim()
      ? message
      : SESSION_EXPIRED_MESSAGE
  );

  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem(AUTH_PROVIDER_KEY);
  localStorage.removeItem(MICROSOFT_SESSION_USER_KEY);
  localStorage.removeItem(AZURE_SSO_SESSION_KEY);
  sessionStorage.removeItem(MS_LOGIN_PENDING_KEY);
  sessionStorage.removeItem(MS_LOGIN_EMAIL_KEY);

  const loginUrl = buildLoginUrl(
    window.location.pathname,
    window.location.search
  );
  window.location.replace(`${window.location.origin}${loginUrl}`);
  return true;
}

export const rtkQueryServiceTags = {
  PROGRAM: "Program",
  GOALS: "Goals",
  MATERIAL: "Material",
  CERTIFICATE: "Certificate",
  CALENDAR: "Calendar",
  CHAT: "Chat",
  REPORT: "Report",
  DEPLOYMENT: "Deployment",
  BULK_UPLOAD: "BulkUpload",
  FEEDBACK: "Feedback",
  FEEDS: "Feeds",
  REPORTS: "Reports",
  INVENTORY: "Inventory",
  INVENTORY_CATALOG: "InventoryCatalog",
  MAINTENANCE: "Maintenance",
  DEFECTS: "Defects",
  WORK_ORDERS: "WorkOrders",
  AUDIT_LOGS: "AuditLogs",
  ALERT_LOGS: "AlertLogs",
  ALERT_RULES: "AlertRules",
  ALERT_RECIPIENTS: "AlertRecipients",
};
const {
  PROGRAM,
  GOALS,
  MATERIAL,
  CERTIFICATE,
  CALENDAR,
  CHAT,
  DEPLOYMENT,
  BULK_UPLOAD,
  INVENTORY,
  INVENTORY_CATALOG,
  MAINTENANCE,
  DEFECTS,
  WORK_ORDERS,
  AUDIT_LOGS,
  ALERT_LOGS,
  ALERT_RULES,
  ALERT_RECIPIENTS,
} = rtkQueryServiceTags;

const rawBaseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers) => {
    const token = localStorage.getItem("access_token");

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    headers.set("Region", region);

    return headers;
  },
});

async function retryWithMicrosoftToken(args, api, extraOptions) {
  const microsoftSession =
    localStorage.getItem(AUTH_PROVIDER_KEY) === MICROSOFT_AUTH_PROVIDER;

  if (!microsoftSession || extraOptions?._msRetry) {
    return null;
  }

  try {
    const { refreshMicrosoftAccessToken } = await import("../auth/microsoftAuth");
    await refreshMicrosoftAccessToken();
    return rawBaseQuery(args, api, { ...extraOptions, _msRetry: true });
  } catch (error) {
    console.error("Microsoft token refresh error:", error);
    return null;
  }
}

export const rtkQueryApiServices = createApi({
  reducerPath: "rtkQueryApiServices",
  baseQuery: async (args, api, extraOptions) => {
    let result = await rawBaseQuery(args, api, extraOptions);

    if (result?.error?.status === 401) {
      const retried = await retryWithMicrosoftToken(args, api, extraOptions);
      if (retried) {
        result = retried;
      }
    }

    if (result?.error?.data && handleSessionExpired(result.error.data)) {
      return result;
    }

    if (result?.error?.status === 403) {
      const message =
        result.error?.data?.error ||
        result.error?.data?.message ||
        result.error?.data?.detail ||
        "You do not have permission to perform this action.";
      toast.error(
        typeof message === "string"
          ? message
          : "You do not have permission to perform this action."
      );
    }

    return result;
  },
  tagTypes: [
    PROGRAM,
    GOALS,
    MATERIAL,
    CERTIFICATE,
    CALENDAR,
    CHAT,
    DEPLOYMENT,
    BULK_UPLOAD,
    INVENTORY,
    INVENTORY_CATALOG,
    MAINTENANCE,
    DEFECTS,
    WORK_ORDERS,
    AUDIT_LOGS,
    ALERT_LOGS,
    ALERT_RULES,
    ALERT_RECIPIENTS,
  ], // Define the necessary tags
  endpoints: () => ({}),
});

const api = axios.create({
  baseURL: baseUrl,
});

api.interceptors.request.use(function (config) {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers.Region = region;
  return config;
});

api.interceptors.response.use(
  (response) => {
    // Some APIs return 200 with an error payload for expired sessions
    if (response?.data && handleSessionExpired(response.data)) {
      return Promise.reject(response);
    }

    if (
      response.status &&
      (response.status === 200 || response.status === 201)
    ) {
      return {
        data: response.data,
        status: response.status,
      };
    }
    return response;
  },
  async (error) => {
    if (error?.response?.data && handleSessionExpired(error.response.data)) {
      return Promise.reject(error);
    }

    const reasons = ["ERR_BAD_REQUEST", "ERR_NETWORK", "ERR_BAD_RESPONSE"];
    const errMsg =
      error?.response?.data?.errors?.[0] ??
      error?.response?.data?.error ??
      error?.response?.data?.message ??
      error?.response?.data?.errors?.non_field_errors?.[0];
    if (errMsg?.length > 0) {
      if (
        errMsg !==
        "Please verify your email address. A verification link has been sent to your registered email address."
      ) {
        toast.error(errMsg);
      }
    } else if (error?.response?.status === 403) {
      toast.error("You do not have permission to perform this action.");
    }
    if (
      error.code &&
      (error.code === "ERR_NETWORK" || error.code === "ERR_BAD_RESPONSE")
    ) {
      error.message = "There is a Server Error. Please try again later.";
    }
    if (
      error.code &&
      error.code === "ERR_BAD_REQUEST" &&
      error.response.status === 401
    ) {
      const microsoftSession =
        localStorage.getItem(AUTH_PROVIDER_KEY) === MICROSOFT_AUTH_PROVIDER;

      if (microsoftSession && !error.config?._msRetry) {
        try {
          const { refreshMicrosoftAccessToken } = await import(
            "../auth/microsoftAuth"
          );
          const token = await refreshMicrosoftAccessToken();
          error.config._msRetry = true;
          error.config.headers = error.config.headers || {};
          error.config.headers.Authorization = `Bearer ${token}`;
          return axios(error.config);
        } catch (refreshError) {
          console.error("Microsoft token refresh error:", refreshError);
        }
      }

      if (localStorage.getItem("access_token")) {
        window.location.replace(window.location.origin + "/logout");
      }
      error.message =
        error.response.data?.error || error.response.data?.message;
    }
    if (
      error.code &&
      error.code === "ERR_BAD_REQUEST" &&
      error.response.status === 400
    ) {
      error.message =
        error.response.data?.error ||
        error.response.data?.message ||
        error.response.data?.email[0];
    }
    if (
      !reasons.includes(error.code) &&
      error?.response?.status === 401 &&
      !refresh
    ) {
      refresh = true;
      try {
        const response = await axios.post(
          `${baseUrl}/token/refresh`,
          {
            refresh: localStorage.getItem("refresh_token"),
          },
          {
            headers: {
              "Content-Type": "application/json",
              Region: region,
            },
            withCredentials: true,
          },
        );

        if (response.status === 200) {
          axios.defaults.headers.common["Authorization"] =
            `Bearer ${response.data["access"]}`;
          localStorage.setItem("access_token", response.data.access);
          localStorage.setItem("refresh_token", response.data.refresh);
          return axios(error.config);
        }
      } catch (error) {
        console.error("Token refresh error:", error);
      }
    }
    refresh = false;
    return Promise.reject(error);
  },
);

export default api;
